import React, { useState } from 'react';
import { Button, Form, Row, Col, Container, Alert } from 'react-bootstrap';
import Select from 'react-select';
import api from '../utils/api';
import debounce from 'lodash.debounce';

const MergeMembers = () => {
    const [members, setMembers] = useState([]);
    const [primaryMember, setPrimaryMember] = useState(null);
    const [duplicateMember, setDuplicateMember] = useState(null);
    const [isMerging, setIsMerging] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // Debounced function to handle member search
    const debouncedSearch = debounce(async (input) => {
        if (!input) return;

        setLoading(true);
        try {
            const response = await api.get(`/api/members?search=${input}`);
            const options = response.data.data.map(member => {
                const hasSpouseName = member.first2 || member.middle2 || member.last2;
                const spouseName = hasSpouseName 
                    ? ` Spouse Name: ${member.first2 ? member.first2 + ' ' : ''}${member.middle2 ? member.middle2 + ' ' : ''}${member.last2 ? member.last2 : ''}`
                    : '';
                const phone1 = member.phone1 ? `Phone 1: ${member.phone1}` : '';
                const phone2 = member.phone2 ? `Phone 2: ${member.phone2}` : '';
                const phoneInfo = [phone1, phone2].filter(Boolean).join(' ');

                return {
                    value: member.memberId,
                    label: `Name: ${member.first1} ${member.middle1} ${member.last1}${spouseName} Address: ${member.address} ${member.city} ${phoneInfo} (ID: ${member.memberId})`,
                };
            });
            setMembers(options);
        } catch (error) {
            console.error('Error fetching members:', error);
        } finally {
            setLoading(false);
        }
    }, 500);

    const handleSearchChange = (inputValue) => {
        debouncedSearch(inputValue);
    };

    const handleMerge = async () => {
        if (!primaryMember || !duplicateMember) {
            setResponseMessage('Please select both a Primary and Duplicate member.');
            return;
        }

        if (primaryMember.value === duplicateMember.value) {
            setResponseMessage('Primary and Duplicate members cannot be the same.');
            return;
        }

        setIsMerging(true);

        try {
            const response = await api.post('/api/mergeMembers', {
                primaryMemberId: primaryMember.value,
                duplicateMemberId: duplicateMember.value,
            });

            setResponseMessage(response.data.msg);

            // Refetch the member list to update the dropdowns
            debouncedSearch('');

            // Reset the form
            setPrimaryMember(null);
            setDuplicateMember(null);
        } catch (error) {
            console.error('Error merging members:', error);
            setResponseMessage(`Failed to merge members: ${error.response?.data?.msg || 'Unknown error'}`);
        } finally {
            setIsMerging(false);
        }
    };

    return (
        <Container className="mt-5">
            <h2>Merge Members</h2>
            {responseMessage && (
                <Alert variant={responseMessage.includes('successfully') ? 'success' : 'danger'}>
                    {responseMessage}
                </Alert>
            )}
            <Form>
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="primaryMember">
                            <Form.Label>Select Primary Member</Form.Label>
                            <Select
                                isLoading={loading}
                                options={members}
                                onInputChange={handleSearchChange}
                                onChange={setPrimaryMember}
                                value={primaryMember}
                                placeholder="Search and select a Primary Member..."
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="duplicateMember">
                            <Form.Label>Select Duplicate Member</Form.Label>
                            <Select
                                isLoading={loading}
                                options={members}
                                onInputChange={handleSearchChange}
                                onChange={setDuplicateMember}
                                value={duplicateMember}
                                placeholder="Search and select a Duplicate Member..."
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Button 
                    variant="primary" 
                    onClick={handleMerge} 
                    disabled={!primaryMember || !duplicateMember || isMerging}
                >
                    {isMerging ? 'Merging...' : 'Merge Members'}
                </Button>
            </Form>
        </Container>
    );
};

export default MergeMembers;